<main class="landing-page">
  <section class="landing-shell">
    <header class="landing-topbar glass-card">
      <div class="brand-area">
        <div class="brand-logo"><img src="/favicon.png" alt="Gherkin Scenario Forge" /></div>
        <div class="brand-copy">
          <div class="brand-title">Gherkin Scenario Forge</div>
          <div class="brand-subtitle">AI-powered QA decision platform</div>
        </div>
      </div>

      <div class="hero-actions">
        <a class="secondary-button login-topbar-button" routerLink="/login">Sign in</a>
        <button class="secondary-button" type="button" (click)="talkToSales()">Talk to sales</button>
        <button class="landing-cart-button" type="button" (click)="openCartDrawer()">
          <span class="landing-cart-button__icon">+</span>
          <span>Cart</span>
          <span class="landing-cart-button__badge">{{ cartSeatCount() }}</span>
        </button>
        <button class="primary-button" type="button" (click)="navigateToPricing()">Build your plan</button>
      </div>
    </header>

    <section class="hero-grid hero-grid--stacked">
      <section class="hero-copy-panel">
        <div class="eyebrow">QA decision platform</div>
        <h1 class="hero-title hero-title--wide">From ambiguous requirements to reliable QA decisions.</h1>
        <p class="hero-copy login-hero-copy">
          Gherkin Scenario Forge transforms requirements, scenarios and code changes into actionable QA decisions for product, QA and delivery teams.
        </p>

        <div class="hero-cta-row">
          <button class="primary-button hero-primary-button" type="button" (click)="navigateToPricing()">
            Build your plan
          </button>
          <button class="secondary-button" type="button" (click)="talkToSales()">Talk to sales</button>
          <button class="primary-button hero-primary-button" type="button" (click)="navigateToReview()">
            CI/CD Review
          </button>
        </div>

        <article class="product-stage glass-card product-stage--overview">
          <div class="stage-window-chrome">
            <span></span>
            <span></span>
            <span></span>
          </div>

          <div class="product-stage-head product-stage-head--overview">
            <div>
              <div class="hero-proof-label">Platform preview</div>
              <h2>One workflow from product behavior to QA action.</h2>
              <p class="product-stage-copy">
                See how product behavior becomes challenged requirements, deterministic scenarios, automation assets, audit signals and merge request guidance.
              </p>
            </div>
          </div>

          <div class="hero-preview-grid">
            <section class="hero-preview-card hero-preview-card--signal premium-signal-flow">
              <div class="hero-preview-card-head">
                <div class="stage-panel-label">Live QA output</div>
                <span class="hero-preview-inline-label">CI/CD Review for Merge Requests</span>
              </div>

              <div class="signal-step signal-step--change" style="--signal-order: 0">
                <span class="signal-step-label">Change detected</span>
                <ul class="signal-step-list">
                  <li *ngFor="let item of heroPreviewChangeItems">{{ item }}</li>
                </ul>
              </div>

              <div class="signal-connector" aria-hidden="true" style="--signal-order: 1">↓</div>

              <div class="signal-step signal-step--analysis" style="--signal-order: 2">
                <span class="signal-step-label">QA analysis</span>
                <p>Challenging requirements, coverage, scenarios and risk impact</p>
              </div>

              <div class="signal-connector" aria-hidden="true" style="--signal-order: 3">↓</div>

              <div class="signal-step signal-step--impact" style="--signal-order: 4">
                <div class="hero-preview-alert">QA impact detected</div>
                <ul class="hero-preview-list">
                  <li *ngFor="let signal of heroPreviewSignals">{{ signal }}</li>
                </ul>
              </div>

              <div class="signal-connector" aria-hidden="true" style="--signal-order: 5">↓</div>

              <div class="signal-step signal-step--action" style="--signal-order: 6">
                <span class="signal-step-label">Scenario to add</span>
                <pre>{{ heroPreviewActionScenario }}</pre>
              </div>
            </section>

            <div class="hero-signal-grid">
              <article class="signal-card">
                <div class="hero-proof-label">Requirement challenge</div>
                <h2>Clarify business rules before scenario generation</h2>
                <p>Detect ambiguous wording, missing acceptance criteria and untestable outcomes before they become fragile tests.</p>
              </article>
              <article class="signal-card">
                <div class="hero-proof-label">Deterministic scenarios</div>
                <h2>Generate scenarios from validated product behavior</h2>
                <p>Turn challenged requirements, business rules and user flows into structured Gherkin scenarios built for clarity and execution.</p>
              </article>
              <article class="signal-card">
                <div class="hero-proof-label">Explainable QA analysis</div>
                <h2>Audit what your suite misses</h2>
                <p>Reveal weak coverage, fragile scenarios and risk areas with explainable QA signals your team can act on.</p>
              </article>
              <article class="signal-card">
                <h2>Generate automation-ready outputs</h2>
                <p>Convert scenarios into implementation assets for Playwright or Selenium without rewriting everything manually.</p>
              </article>
            </div>
          </div>
        </article>
      </section>
    </section>

    <section class="content-section problem-section">
      <div class="section-heading">
        <div class="eyebrow">Problem</div>
        <h2 class="section-title">Why QA breaks down</h2>
        <p class="section-copy">
          Most QA risk starts before test execution: unclear requirements, weak scenario evidence and disconnected delivery signals.
        </p>
      </div>

      <div class="problem-grid">
        <article
          class="capability-card problem-card"
          *ngFor="let problem of problemPoints; index as index"
        >
          <div class="capability-card-top">
            <span class="capability-index">{{ index + 1 < 10 ? '0' + (index + 1) : index + 1 }}</span>
            <span class="hero-proof-label">Breakdown</span>
          </div>
          <h3>{{ problem.title }}</h3>
          <p>{{ problem.description }}</p>
        </article>
      </div>
    </section>

    <section class="content-section transformation-section">
      <div class="section-heading">
        <div class="eyebrow">HOW IT WORKS</div>
        <h2 class="section-title">From vague testing inputs to reliable QA outputs</h2>
        <p class="section-copy">
          The platform moves QA from uncertain inputs to traceable decisions through one connected pipeline.
        </p>
      </div>

      <article class="transformation-card transformation-card--transformation transformation-card--wide">
        <div class="transformation-card-head">
          <div class="transformation-label">Pipeline</div>
        </div>
        <h3>Requirements → Challenge → Scenarios → Automation → Audit → CI/CD Review</h3>
        <p>
          Each step keeps business intent connected to deterministic QA evidence, so teams can see what is ready, what is weak and what needs action before release.
        </p>

        <div class="transformation-visual transformation-visual--pipeline">
          <div class="premium-pipeline" aria-label="QA workflow pipeline">
            <ng-container *ngFor="let step of premiumPipelineSteps; let index = index; let last = last">
              <div
                class="premium-pipeline__step premium-pipeline__step--active"
                [class.premium-pipeline__step--featured]="step.featured"
                [style.--pipeline-order]="index * 2"
              >
                <div class="premium-pipeline__node">
                  <span class="premium-pipeline__index">{{ step.index }}</span>
                  <span class="premium-pipeline__label">{{ step.label }}</span>
                </div>
              </div>

              <div
                class="premium-pipeline__connector"
                *ngIf="!last"
                aria-hidden="true"
                [style.--pipeline-order]="index * 2 + 1"
              >
                <span class="premium-pipeline__connector-line"></span>
                <span class="premium-pipeline__connector-dot"></span>
              </div>
            </ng-container>
          </div>
        </div>
      </article>
    </section>

    <section class="content-section product-workflow-section">
      <div class="section-heading">
        <div class="eyebrow">Product workflow</div>
        <h2 class="section-title">One continuous QA workflow</h2>
        <p class="section-copy">
          Each module accepts the artifact your team already has and returns an output that can drive the next QA decision.
        </p>
      </div>

      <div class="workflow-module-grid">
        <article
          class="capability-card workflow-module-card"
          *ngFor="let module of productWorkflowModules; index as index"
          [class.capability-card--featured]="module.featured"
        >
          <div class="capability-card-top">
            <span class="capability-index">{{ index + 1 < 10 ? '0' + (index + 1) : index + 1 }}</span>
            <span class="hero-proof-label">Module</span>
          </div>
          <h3>{{ module.title }}</h3>
          <div class="workflow-module-meta">
            <div>
              <span>Input</span>
              <strong>{{ module.input }}</strong>
            </div>
            <div>
              <span>Platform action</span>
              <strong>{{ module.action }}</strong>
            </div>
            <div>
              <span>Output</span>
              <strong>{{ module.output }}</strong>
            </div>
          </div>
        </article>
      </div>
    </section>

    <section class="content-section proof-platform-section">
      <div class="section-heading">
        <div class="eyebrow">Proof</div>
        <h2 class="section-title">See the platform in action</h2>
        <p class="section-copy">
          Concrete examples show how the same workflow improves scenario quality, exposes coverage gaps and turns merge request changes into QA action.
        </p>
      </div>

      <div class="scenario-proof-grid">
        <article class="scenario-proof-card scenario-proof-card--before">
          <div class="scenario-proof-label">Before</div>
          <h3>Weak scenario</h3>
          <p>Too vague to trust, too incomplete to automate reliably.</p>
          <pre class="scenario-proof-code">{{ scenarioProofBefore }}</pre>
        </article>

        <article class="scenario-proof-card scenario-proof-card--after">
          <div class="scenario-proof-label">After</div>
          <h3>Improved scenario</h3>
          <p>Clearer inputs, explicit action, stronger assertions and deterministic expected results.</p>
          <pre class="scenario-proof-code">{{ scenarioProofAfter }}</pre>
        </article>
      </div>

      <div class="scenario-proof-benefits">
        <span *ngFor="let benefit of scenarioProofBenefits">{{ benefit }}</span>
      </div>

      <div class="proof-action-grid">
        <article class="audit-proof-card audit-proof-card--result">
          <div class="audit-proof-label">Coverage audit</div>
          <h3>Audit coverage result</h3>

          <div class="audit-proof-result-grid">
            <section
              class="audit-proof-result-block"
              *ngFor="let block of auditProofBlocks"
              [class.audit-proof-result-block--covered]="block.tone === 'covered'"
              [class.audit-proof-result-block--missing]="block.tone === 'missing'"
              [class.audit-proof-risk]="block.tone === 'risk'"
            >
              <strong>{{ block.title }}</strong>
              <ul *ngIf="block.items?.length">
                <li *ngFor="let item of block.items">{{ item }}</li>
              </ul>
              <p *ngIf="block.value">{{ block.value }}</p>
            </section>
          </div>
        </article>

        <article class="audit-proof-card audit-proof-card--result">
          <div class="audit-proof-label">CI/CD impact</div>
          <h3>Merge request QA decision</h3>
          <div class="transformation-after-signal">
            <strong>QA impact detected</strong>
            <ul class="transformation-signal-list">
              <li *ngFor="let signal of heroPreviewSignals">{{ signal }}</li>
            </ul>
          </div>

          <div class="transformation-after-scenario">
            <div class="transformation-after-scenario__label">Scenario to add</div>
            <pre class="transformation-after-scenario__content">{{ afterScenarioContent }}</pre>
          </div>
        </article>
      </div>
    </section>

    <section class="content-section integrations-section">
      <div class="section-heading">
        <div class="eyebrow">Integrations</div>
        <h2 class="section-title">Connect product, test and delivery systems</h2>
        <p class="section-copy">
          Gherkin Scenario Forge connects Jira, Squash TM and GitLab so QA decisions stay linked from requirement to merge request.
        </p>
      </div>

      <div class="integration-grid">
        <article
          class="capability-card integration-card"
          *ngFor="let integration of integrationCards"
        >
          <div class="capability-card-top">
            <span class="capability-index">{{ integration.name }}</span>
            <span class="hero-proof-label">{{ integration.category }}</span>
          </div>
          <h3>{{ integration.description }}</h3>
          <ul class="integration-list">
            <li *ngFor="let item of integration.bullets">
              <span aria-hidden="true">→</span>
              <strong>{{ item }}</strong>
            </li>
          </ul>
          <button class="primary-button integration-card__button" type="button" (click)="navigateToIntegration(integration.action)">
            {{ integration.ctaLabel }}
          </button>
        </article>
      </div>
    </section>

    <section #pricingSection class="content-section pricing-section">
      <div class="section-heading">
        <div class="eyebrow">Pricing</div>
        <h2 class="section-title">{{ pricingSectionTitle() }}</h2>
        <p class="section-copy">{{ pricingSectionCopy() }}</p>
      </div>

      <div class="pricing-grid">
        <article class="pricing-card" *ngFor="let plan of pricingPlans()" [class.pricing-card-highlight]="plan.highlight">
          <div class="pricing-card-content">
            <div class="pricing-card-head">
              <div class="pricing-plan-meta">
                <div class="pricing-plan-row">
                  <h3>{{ plan.name }}</h3>
                  <span class="pricing-badge" *ngIf="plan.badge">{{ plan.badge }}</span>
                </div>
                <p>{{ plan.description }}</p>
              </div>
              <div class="pricing-price">
                <ng-container *ngIf="!plan.salesOnly; else customPlanPrice">
                  <span class="pricing-price-amount">{{ plan.unitPrice }}€</span>
                  <span class="pricing-price-period">/ user / month</span>
                </ng-container>
                <ng-template #customPlanPrice>
                  <span class="pricing-price-amount">{{ plan.priceLabel }}</span>
                </ng-template>
              </div>
            </div>

            <ul class="pricing-feature-list">
              <li *ngFor="let feature of plan.features">{{ feature }}</li>
            </ul>
          </div>

          <div class="pricing-card-footer">
            <section class="plan-quantity-panel" *ngIf="!plan.salesOnly; else salesOnlyPlan">
              <div class="pricing-builder-meta">
                <span class="login-trust-label">Users on this plan</span>
                <strong>{{ cartQuantity(plan.key) }}</strong>
              </div>

              <div class="quantity-stepper">
                <button type="button" (click)="decrementPlan(plan.key)" [attr.aria-label]="'Decrease ' + plan.name">-</button>
                <input
                  type="number"
                  min="0"
                  step="1"
                  inputmode="numeric"
                  [value]="cartQuantity(plan.key)"
                  (input)="updatePlanQuantity(plan.key, $event)"
                  [attr.aria-label]="plan.name + ' quantity'"
                />
                <button type="button" (click)="incrementPlan(plan.key)" [attr.aria-label]="'Increase ' + plan.name">+</button>
              </div>

              <div class="pricing-card-total">{{ planMonthlyTotal(plan.key) }}</div>
            </section>
            <ng-template #salesOnlyPlan>
              <section class="plan-quantity-panel">
                <div class="pricing-builder-meta">
                  <span class="login-trust-label">Rollout model</span>
                  <strong>Sales-assisted</strong>
                </div>
                <div class="pricing-card-total">Custom rollout</div>
              </section>
            </ng-template>

            <div class="pricing-actions">
              <button class="primary-button primary-button--wide" type="button" (click)="addPlanAndOpenCart(plan.key)" *ngIf="!plan.salesOnly; else salesOnlyCta">
                Build your plan
              </button>
              <ng-template #salesOnlyCta>
                <button class="primary-button primary-button--wide" type="button" (click)="talkToSales(plan.key)">
                  Plan with sales
                </button>
              </ng-template>
              <button class="secondary-button primary-button--wide" type="button" (click)="talkToSales(plan.key)">
                Talk to sales
              </button>
            </div>
          </div>
        </article>
      </div>

      <section class="commercial-cta-card glass-card">
        <div class="commercial-cta-card__header">
          <div>
            <div class="eyebrow">Call to action</div>
            <h2 class="subsection-title">{{ commercialCtaTitle() }}</h2>
          </div>
          <div class="status-pill status-pill-success">{{ commercialStatusLabel() }}</div>
        </div>

        <div class="commercial-cta-card__body">
          <p class="section-copy">{{ commercialCtaCopy() }}</p>

          <div class="cta-stack">
            <button class="primary-button commercial-cta-card__button" type="button" (click)="openCartDrawer()">
              Build your plan
            </button>
            <button class="secondary-button commercial-cta-card__button" type="button" (click)="talkToSales()">
              Talk to sales
            </button>
          </div>
        </div>

        <div class="access-meta-grid">
          <div class="access-meta-item access-meta-item-dark">
            <span class="login-trust-label">Purchasing</span>
            <strong>Company-based seat purchasing</strong>
          </div>
          <div class="access-meta-item access-meta-item-dark">
            <span class="login-trust-label">Seat assignment</span>
            <strong>One seat assigned per user</strong>
          </div>
        </div>
      </section>
    </section>

    <section class="content-section custom-automation-section">
      <section class="custom-automation-card glass-card" aria-labelledby="custom-test-automation-title">
        <div class="custom-automation-copy">
          <div class="eyebrow">Premium consulting</div>
          <h2 id="custom-test-automation-title" class="section-title">Custom test automation</h2>
          <p class="custom-automation-subtitle">
            Tailored support to generate tests adapted to your repository, architecture and business-critical journeys.
          </p>
          <p class="section-copy">
            We analyze your repository, project structure, code conventions and critical journeys to design a test generation strategy that truly fits your product.
          </p>
        </div>

        <div class="custom-automation-panel">
          <div class="custom-automation-price">
            <span>Starting at</span>
            <strong>2 000 €</strong>
          </div>

          <ul class="custom-automation-list">
            <li *ngFor="let item of customTestAutomationItems">
              <span class="custom-automation-list__icon" aria-hidden="true">✓</span>
              <span>{{ item }}</span>
            </li>
          </ul>

          <button class="primary-button primary-button--wide" type="button" (click)="requestCustomTestAutomationConsulting()">
            Request custom support
          </button>
        </div>
      </section>
    </section>

    <section #contactSection class="content-section contact-section">
      <section class="contact-commercial-shell contact-commercial-shell--secondary glass-card">
        <div class="contact-commercial-grid">
        <div class="contact-commercial-copy">
            <div class="eyebrow">Commercial contact</div>
            <h2 class="subsection-title">{{ salesContactTitle() }}</h2>
            <p class="section-copy">{{ salesContactCopy() }}</p>

            <div class="contact-commercial-highlights">
              <div class="contact-commercial-highlight">
                <span class="login-trust-label">Use case</span>
                <strong>Pricing, rollout and enterprise purchasing</strong>
              </div>
              <div class="contact-commercial-highlight">
                <span class="login-trust-label">Best for</span>
                <strong>Teams that need help beyond self-serve checkout</strong>
              </div>
            </div>
          </div>

          <form class="contact-commercial-form" [formGroup]="contactForm" (ngSubmit)="submitContactRequest()" novalidate>
            <label class="contact-field contact-field--full">
              <span>Subject</span>
              <input type="text" formControlName="subject" autocomplete="off" />
              <small class="contact-field-error" *ngIf="fieldError('subject')">{{ fieldError('subject') }}</small>
            </label>

            <div class="contact-form-grid">
              <label class="contact-field">
                <span>First name</span>
                <input type="text" formControlName="firstName" autocomplete="given-name" />
                <small class="contact-field-error" *ngIf="fieldError('firstName')">{{ fieldError('firstName') }}</small>
              </label>

              <label class="contact-field">
                <span>Last name</span>
                <input type="text" formControlName="lastName" autocomplete="family-name" />
                <small class="contact-field-error" *ngIf="fieldError('lastName')">{{ fieldError('lastName') }}</small>
              </label>

              <label class="contact-field">
                <span>Company</span>
                <input type="text" formControlName="companyName" autocomplete="organization" />
                <small class="contact-field-error" *ngIf="fieldError('companyName')">{{ fieldError('companyName') }}</small>
              </label>

              <label class="contact-field">
                <span>Email</span>
                <input type="email" formControlName="workEmail" autocomplete="email" />
                <small class="contact-field-error" *ngIf="fieldError('workEmail')">{{ fieldError('workEmail') }}</small>
              </label>

              <label class="contact-field">
                <span>Phone</span>
                <input type="tel" formControlName="phoneNumber" autocomplete="tel" />
                <small class="contact-field-error" *ngIf="fieldError('phoneNumber')">{{ fieldError('phoneNumber') }}</small>
              </label>

              <label class="contact-field">
                <span>Job title</span>
                <input type="text" formControlName="jobTitle" autocomplete="organization-title" />
                <small class="contact-field-error" *ngIf="fieldError('jobTitle')">{{ fieldError('jobTitle') }}</small>
              </label>
            </div>

            <div class="contact-form-grid contact-form-grid--licenses">
              <label class="contact-field" *ngFor="let plan of pricingPlans()">
                <span>{{ plan.name }} qty</span>
                <input type="number" [formControl]="requestedPlanControl(plan.key)" min="0" step="1" inputmode="numeric" />
                <small class="contact-field-error" *ngIf="fieldError('requestedPlans.' + plan.key)">{{ fieldError('requestedPlans.' + plan.key) }}</small>
              </label>
            </div>

            <label class="contact-field contact-field--full">
              <span>Message / Questions</span>
              <textarea formControlName="message" rows="6"></textarea>
              <small class="contact-field-error" *ngIf="fieldError('message')">{{ fieldError('message') }}</small>
            </label>

            <div class="contact-feedback contact-feedback--success" *ngIf="submitSuccess()">
              Your commercial request has been sent successfully.
            </div>

            <div class="contact-feedback contact-feedback--error" *ngIf="submitError()">
              {{ submitError() }}
            </div>

            <div class="contact-form-actions">
              <button class="primary-button contact-submit-button" type="submit" [disabled]="submitting()">
                {{ submitting() ? 'Sending...' : 'Send commercial request' }}
              </button>
              <div class="contact-form-note">
                Devlore uses these business contact details only to handle your commercial request and related pre-contractual exchanges.
                See our
                <a [routerLink]="privacyPolicyUrl">Privacy policy</a>
                and
                <a [routerLink]="termsOfServiceUrl">Terms of service</a>.
              </div>
            </div>
          </form>
        </div>
      </section>
    </section>

    <footer class="public-legal-footer glass-card" aria-label="Legal footer">
      <div class="public-legal-footer__copy">
        <strong>{{ legalCompanyName }}</strong>
        <p>
          Gherkin Scenario Forge is operated by {{ legalCompanyName }} and powered by {{ legalHostingProvider }}.
        </p>
      </div>

      <nav class="public-legal-footer__links" aria-label="Legal links">
        <a class="public-legal-footer__link" [routerLink]="privacyPolicyUrl">Privacy policy</a>
        <a class="public-legal-footer__link" [routerLink]="termsOfServiceUrl">Terms of service</a>
      </nav>
    </footer>
  </section>
</main>
<app-pricing-cart-drawer />
